// Page-range pill utilities.
//
// Selected page numbers are displayed as a compact range string in the rail
// search bar pill (e.g., `1-3, 5, 7-9`). The user can also edit that string
// to drive selection. Both directions live here so they stay in sync.

/**
 * Collapse a list of page numbers into a compact range string.
 * - Sorts and dedupes the input.
 * - Contiguous runs of length >= 2 become `start-end`; singletons stay bare.
 * Returns an empty string for an empty list.
 */
export function formatPageRanges(numbers: number[]): string {
    if (numbers.length === 0) return '';
    const sorted = Array.from(new Set(numbers.filter(n => Number.isInteger(n)))).sort((a, b) => a - b);
    const parts: string[] = [];
    let runStart = sorted[0];
    let runEnd = sorted[0];
    for (let i = 1; i < sorted.length; i += 1) {
        const n = sorted[i];
        if (n === runEnd + 1) {
            runEnd = n;
        } else {
            parts.push(runStart === runEnd ? `${runStart}` : `${runStart}-${runEnd}`);
            runStart = n;
            runEnd = n;
        }
    }
    parts.push(runStart === runEnd ? `${runStart}` : `${runStart}-${runEnd}`);
    return parts.join(', ');
}

/**
 * Parse a page-range pill text into a sorted, deduped list of page numbers.
 *
 * Grammar (whitespace-tolerant):
 *   list := segment ("," segment)*
 *   segment := N | N "-" N    (both N positive integers; N1 <= N2 in a range)
 *
 * Returns `null` for any of:
 *   - empty/whitespace-only input
 *   - tokens that aren't integers or `N-N` ranges
 *   - ranges with start > end
 *   - any page number outside [1, max] (if `max` is provided)
 */
export function parsePageRanges(text: string, max?: number): number[] | null {
    const trimmed = text.trim();
    if (!trimmed) return null;

    const segments = trimmed.split(',').map(s => s.trim()).filter(s => s.length > 0);
    if (segments.length === 0) return null;

    const collected = new Set<number>();

    for (const segment of segments) {
        const rangeMatch = segment.match(/^(\d+)\s*-\s*(\d+)$/);
        if (rangeMatch) {
            const start = Number.parseInt(rangeMatch[1], 10);
            const end = Number.parseInt(rangeMatch[2], 10);
            if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
            if (start < 1 || end < 1 || start > end) return null;
            if (max != null && (start > max || end > max)) return null;
            for (let n = start; n <= end; n += 1) collected.add(n);
            continue;
        }
        const single = segment.match(/^(\d+)$/);
        if (single) {
            const n = Number.parseInt(single[1], 10);
            if (!Number.isFinite(n) || n < 1) return null;
            if (max != null && n > max) return null;
            collected.add(n);
            continue;
        }
        return null;
    }

    return Array.from(collected).sort((a, b) => a - b);
}

/**
 * Given the page order BEFORE a move and the ids being moved, return the id of
 * the page the moved chunk should land AFTER to restore the original order — the
 * first non-moving page preceding the first moved page — or null when the chunk
 * started at the document head (undo should move it back to the start).
 *
 * Used to build the "undo move" action from the pre-move ordering.
 */
export function computeUndoAnchor(
    prePages: { id: string }[],
    movingIds: string[],
): string | null {
    const movingSet = new Set(movingIds);
    const firstMovedIdx = prePages.findIndex(p => movingSet.has(p.id));
    for (let i = firstMovedIdx - 1; i >= 0; i -= 1) {
        if (!movingSet.has(prePages[i].id)) {
            return prePages[i].id;
        }
    }
    return null;
}
